import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../components/Common/Button';
import TemplateCard from '../components/ResumeBuilder/TemplateCard';
import { setTemplate, setResume } from '../redux/Slices/ResumeSlice';
import {
  useFetchResumeTemplatesQuery,
  useUpdateResumeMutation,
  useGenerateResumeFromFormMutation,
  useLazyFetchResumeByIdQuery
} from '../services/resumeBuilderApi';

type Props = {
  onNext: () => void;
  onBack: () => void;
};

const TemplateSelectionPage = ({ onNext }: Props) => {
  const dispatch = useDispatch();
  const [isGenerating, setIsGenerating] = useState(false);
  const resume = useSelector((state: any) => state.root.resume);

  /* fetch templates */

  const { data, isLoading, isError } = useFetchResumeTemplatesQuery({});

  const templates = data?.data || [];

  /* API hooks */

  const [updateResume] = useUpdateResumeMutation();

  const [generateResumeFromForm] = useGenerateResumeFromFormMutation();

  const [fetchResumeById] = useLazyFetchResumeByIdQuery();

  /* select template */

  const selectTemplate = (template: any) => {
    dispatch(
      setTemplate({
        templateId: template.id,
        templateVersion: template.templateVersion || 1
      })
    );
  };

  /* MAIN FUNCTION */

  const handleGenerateResume = async () => {
    setIsGenerating(true);
    try {
      /* 1 update resume */
      const id = resume.id; // important for update
      const requestBody = {
        id, // important for update
        title: resume.title || 'My Resume',

        templateId: resume.templateId,

        templateVersion: resume.templateVersion,

        personalInfo: resume.personalInfo,

        skills: resume.skills,

        experience: resume.experience,

        education: resume.education || [],

        languages: resume.languages || [],

        projects: resume.projects,

        certifications: resume.certifications,

        achievements: resume.achievements,

        preferences: resume.preferences,

        additionalInfo: resume.additionalInfo,

        resumeFormState: resume.resumeFormState,

        resumeGenerated: false
      };

      const updateResponse = await updateResume(requestBody).unwrap();

      const resumeId = updateResponse.data; // important

      /* 2 AI generation */

      await generateResumeFromForm(id).unwrap();

      /* 3 fetch generated resume */

      const generatedResume = await fetchResumeById(id).unwrap();

      /* 4 store in redux */

      dispatch(setResume(generatedResume.data));

      /* 5 go preview page */

      onNext();
    } catch (error) {
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className='p-10'>
      <h1 className='text-2xl font-semibold'>Select Template</h1>

      {isLoading && <p className='mt-6'>Loading templates...</p>}

      {isError && <p className='mt-6 text-red-500'>Failed to load templates</p>}

      <div className='mt-8 grid grid-cols-3 gap-8'>
        {templates.map((template: any) => (
          <TemplateCard
            key={template.id}
            template={template}
            selectedId={resume.templateId}
            onSelect={selectTemplate}
          />
        ))}
      </div>

      <div className='mt-10 flex justify-end'>
        <Button
          label={isGenerating ? 'Generating Resume...' : 'Generate Resume'}
          handleClickBtn={handleGenerateResume}
          variant='contained'
          disabled={!resume.templateId || isGenerating}
          isLoading={isGenerating}
          iconPosition='right'
        >
          {!isGenerating && <ArrowRight size={16} />}
        </Button>
      </div>
    </div>
  );
};

export default TemplateSelectionPage;
